// cursorEditorCellPx — pure sizing helpers for the resizable 24×24 custom
// cursor editor grid.
//
// 背景 (作者 staging フィードバック 2026-09-19、custom-cursor-2026-09-19.md の
// follow-up): 「パネルをリサイズして大きくしたい。画面の解像度によっては小さくて
// ドット絵が描きづらい」。エディタは `CursorEditorPanel.tsx` の 24×24 グリッドを
// `EDITOR_CELL_PX` 固定 10px/マスで描いていたが、これを可変にする。
//
// 設計は `previewOverlay.ts` のプレビュー窓リサイズ (`nextPreviewEdgeFromResize` /
// `clampPreviewEdgeToViewport`) と同型 — 「1 セルの px サイズ」を「サムネ最大辺」
// の代わりに resize 対象にしただけ。2 つの入口 (プリセットボタン「小/中/大」/
// パネル右下の角ドラッグ) が同じ `cellPx` state を更新するので、ロジックは
// 1 か所に集約する (Classic `CursorEditorPopup` / Modern `ModernCursorEditorPanel`
// の両方が `useCursorEditorCellPxResize.ts` 経由でこのモジュールを使う)。

import { CURSOR_SIZE } from '@oekaki/protocol';

/** 「小」プリセット (= 既存の固定値 `EDITOR_CELL_PX` と同値、既定はこれを維持). */
export const CURSOR_EDITOR_CELL_PX_SMALL = 10;
/** 「中」プリセット. */
export const CURSOR_EDITOR_CELL_PX_MEDIUM = 16;
/** 「大」プリセット. */
export const CURSOR_EDITOR_CELL_PX_LARGE = 24;

/**
 * 連続リサイズ (角ドラッグ) の下限。**旧固定値 `EDITOR_CELL_PX` (= 10、
 * `CURSOR_EDITOR_CELL_PX_SMALL`) と同値**にする — viewport-fit clamp
 * (`clampCursorEditorCellPxToViewport`) がどんな画面サイズでも「導入前より
 * 小さくなる」ことが絶対に無いようにするための下限保証 (= 非 regression)。
 */
export const MIN_CURSOR_EDITOR_CELL_PX = CURSOR_EDITOR_CELL_PX_SMALL;
/** 連続リサイズの上限 (24×32 = 768px 四方、4K ディスプレイでも十分大きい). */
export const MAX_CURSOR_EDITOR_CELL_PX = 32;

/** 既定値 (= 従来の固定 `EDITOR_CELL_PX` と同値、既存ユーザの見え方は変えない). */
export const DEFAULT_CURSOR_EDITOR_CELL_PX = CURSOR_EDITOR_CELL_PX_SMALL;

/** 絶対範囲 `[MIN, MAX]` へ clamp する純関数 (永続化の read/write で使う). */
export const clampCursorEditorCellPx = (px: number): number => {
  // NaN だけ既定へ倒す (= 壊れ値からの復帰)。+Infinity 等はそのまま
  // Math.min/max で自然に MAX へ寄る (`previewSizePreference.ts` の
  // `clampPreviewEdge` と同じ, 非有限値を一律 default にはしない).
  const n = Number.isNaN(px) ? DEFAULT_CURSOR_EDITOR_CELL_PX : Math.round(px);
  return Math.min(MAX_CURSOR_EDITOR_CELL_PX, Math.max(MIN_CURSOR_EDITOR_CELL_PX, n));
};

/**
 * グリッド (24 セル四方) 以外にエディタの chrome (現在色スウォッチ行 / ペン・消し
 * ゴム・全消し行 / マスの大きさ行 / プレビュー行 / 決定・やめる・外す行 + フロート
 * 自身のヘッダ・padding) が縦に積む分の固定マージン (px)。実測の目安 (=
 * 各行 ~24px × 6 行 + gap/padding) から丸めた値。`previewOverlay.ts` の
 * `PREVIEW_VIEWPORT_MARGIN` と同じ「viewport の短辺から固定マージンを引く」流儀
 * (= 割合ではなく固定 px の方が、chrome の実高さに対して外れにくい)。
 */
export const CURSOR_EDITOR_VIEWPORT_MARGIN_PX = 300;

/**
 * viewport (window innerWidth/innerHeight) から、グリッドが画面に収まる実効上限
 * cellPx を算出する純関数 (`previewOverlay.ts` の `effectivePreviewMaxEdge` と同型)。
 * どんなに小さい viewport でも `MIN_CURSOR_EDITOR_CELL_PX` (= 旧固定値) を下回らない
 * ため、viewport-fit clamp の導入で既存ユーザの見え方が悪化することはない。
 */
export const effectiveCursorEditorCellPxMax = (
  viewportWidth: number,
  viewportHeight: number,
  margin: number = CURSOR_EDITOR_VIEWPORT_MARGIN_PX,
  hardMax: number = MAX_CURSOR_EDITOR_CELL_PX,
): number => {
  const avail = Math.floor((Math.min(viewportWidth, viewportHeight) - margin) / CURSOR_SIZE);
  return Math.min(hardMax, Math.max(MIN_CURSOR_EDITOR_CELL_PX, avail));
};

/**
 * ユーザ希望 cellPx (= 生値) を viewport の実効上限で clamp して、実際に表示/適用
 * する cellPx を返す純関数 (`clampPreviewEdgeToViewport` と同型)。永続値は生値の
 * まま保持し、レンダリング時にこれを通すことで「小画面では縮め、大画面に戻ったら
 * 希望サイズへ復元」を両立する。
 */
export const clampCursorEditorCellPxToViewport = (
  cellPx: number,
  viewportWidth: number,
  viewportHeight: number,
  margin: number = CURSOR_EDITOR_VIEWPORT_MARGIN_PX,
): number => {
  const max = effectiveCursorEditorCellPxMax(viewportWidth, viewportHeight, margin);
  const n = Number.isNaN(cellPx) ? DEFAULT_CURSOR_EDITOR_CELL_PX : Math.round(cellPx);
  return Math.min(max, Math.max(MIN_CURSOR_EDITOR_CELL_PX, n));
};

/**
 * リサイズ開始時に保存しておく "アンカー" (= origin cellPx + pointer 開始位置).
 * `previewOverlay.ts` の `PreviewResizeAnchor` と同型.
 */
export interface CursorEditorResizeAnchor {
  readonly originCellPx: number;
  readonly pointerStart: { readonly x: number; readonly y: number };
}

/**
 * 右下グリップ drag から新しい cellPx (絶対範囲 clamp 済) を計算する純粋関数.
 * `previewOverlay.ts` の `nextPreviewEdgeFromResize` と同じ「対角ドラッグで
 * dx/dy の大きい方を採用する」方式 — 掴んだ角を動かすだけで素直に伸縮する。
 */
export const nextCursorEditorCellPxFromResize = (
  anchor: CursorEditorResizeAnchor,
  pointer: { readonly x: number; readonly y: number },
  min: number = MIN_CURSOR_EDITOR_CELL_PX,
  max: number = MAX_CURSOR_EDITOR_CELL_PX,
): number => {
  const delta = Math.max(pointer.x - anchor.pointerStart.x, pointer.y - anchor.pointerStart.y);
  const deltaCellPx = delta / CURSOR_SIZE;
  return Math.min(max, Math.max(min, Math.round(anchor.originCellPx + deltaCellPx)));
};
